import clsx from 'clsx'
import { setCSSVar } from 'lib/theme/themeUtils'
// oxlint-disable-next-line no-restricted-imports
import { getContentColourUtilityClassName } from 'lib/theme/utilityClassNames'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'

export type SvgIconSize = 'XXS' | 'XS' | 'S' | 'M' | 'L' | 'XL'

export interface SvgIconProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'color' | 'height' | 'width'> {
  colour?: App.Design.ContentColour
  /**
   * Icon size symbol to pixel maps:
   *
   * - `XS` = 16px
   * - `S` = 20px
   * - `M` = 24px
   * - `L` = 32px
   * - `XL` = 40px
   *
   * @default Either inherit from the parent icon size or fall back to M
   */
  size?: SvgIconSize | number
  /**
 * @deprecated use `size` instead
 * */
  height?: string | number
  /**
 * @deprecated use `size` instead
 * */
  width?: string | number
}

export type SvgIconComponent = React.ComponentType<SvgIconProps>

export const SVG_ICON_SIZES: Record<SvgIconSize, string> = {
  XXS: rem(12),
  XS: rem(16),
  S: rem(20),
  M: rem(24),
  L: rem(32),
  XL: rem(40),
}

export const SVG_ICON_SIZE_CSS_VAR = '--svg-icon-size'

export function setSvgIconSize(size: number) {
  return setCSSVar(SVG_ICON_SIZE_CSS_VAR, rem(size))
}

export const StyledSvgIcon = styled.svg<SvgIconProps>`
  flex-shrink: 0;
  height: var(${SVG_ICON_SIZE_CSS_VAR}, ${SVG_ICON_SIZES.M});
  width: var(${SVG_ICON_SIZE_CSS_VAR}, ${SVG_ICON_SIZES.M});
`

export function determineSvgIconSize(size: SvgIconSize | number | undefined): string | undefined {
  if (size === undefined) return
  if (typeof size === 'number') return rem(size)
  return SVG_ICON_SIZES[size]
}

function SvgIcon(props: SvgIconProps) {
  const {
    colour,
    size,
    height,
    width,
    fill = 'currentColor',
    className,
    style,
    ...rest
  } = props

  const mappedSize = determineSvgIconSize(size)

  return <StyledSvgIcon
    {...rest}
    className={clsx(
      className,
      colour ? getContentColourUtilityClassName(colour) : undefined,
    )}
    fill={fill}
    height={height}
    width={width}
    style={{
      ...style,
      [SVG_ICON_SIZE_CSS_VAR]: mappedSize,
    }}
  />
}

export default SvgIcon
